import { useMemo } from 'react'
import { useParams, Link } from '@tanstack/react-router'
import { Button } from './ui/button'
import { mdiCog, mdiEyeOutline, mdiPlus } from '@mdi/js'
import { AppIcon } from './ui/icon'
import { useContractStakeholderTypes } from '../hooks/useQueries'
import { useBreadcrumbs } from '../hooks/useBreadcrumbs'
import { useLocale } from '../contexts/LocaleContext'
import { useTranslation } from 'react-i18next'
import { formatCreated } from '../lib/format'
import { Can } from '../contexts/PermissionsContext'
import { PageHeader } from './shared/PageHeader'
import { SECTION_ICONS } from '../lib/section-icons'

export function ContractStakeholderTypesPage() {
  const { org } = useParams({ strict: false })
  const { locale } = useLocale()
  const { t } = useTranslation('types')

  const { data: stakeholderTypes = [], isLoading, error } = useContractStakeholderTypes(org || 'demo')

  const getStakeholderTypeName = (stakeholderType: any) => {
    if (!stakeholderType?.names) return stakeholderType?.code || 'Unnamed Stakeholder Type'
    const names = stakeholderType.names as Record<string, string>
    return names[locale] || names.en || names.fr || Object.values(names)[0] || stakeholderType.code || 'Unnamed Stakeholder Type'
  }

  const getStakeholderTypeDescription = (_stakeholderType: any) => {
    return ''
  }

  // Set breadcrumbs
  const breadcrumbs = useMemo(() => [
    { title: t('breadcrumb.home'), path: `/${org}` },
    { title: t('breadcrumb.org_settings'), path: `/${org}/settings` },
    { title: t('stakeholder_types.title') }
  ], [org, t])

  useBreadcrumbs(breadcrumbs)

  return (
    <div className="container mx-auto py-8 max-w-6xl">
      <div className="bg-card rounded-lg shadow-lg">
        <div className="px-6 py-4 border-b border-border">
          <PageHeader
            icon={SECTION_ICONS.stakeholderTypes}
            title={t('stakeholder_types.title')}
            description={t('stakeholder_types.subtitle')}
            actions={
          <Can perm="configuration.write">
            <Link to={`/${org}/contract-stakeholder-types/create` as any}>
              <Button data-testid="create-stakeholder-type">
                <AppIcon path={mdiPlus} size="sm" className="mr-2" />
                {t('stakeholder_types.create')}
              </Button>
            </Link>
          </Can>
            }
          />
        </div>

        <div className="p-6">
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-medium text-foreground">{t('stakeholder_types.list_title')}</h2>
              <p className="text-muted-foreground text-sm mt-1">
                {t('stakeholder_types.list_subtitle')}
              </p>
            </div>

            <div className="bg-card shadow overflow-hidden rounded-md border border-border">
              <table className="min-w-full divide-y divide-border">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-6 py-3 text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                      {t('stakeholder_types.column_name')}
                    </th>
                    <th className="px-6 py-3 text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                      {t('stakeholder_types.column_code')}
                    </th>
                    <th className="px-6 py-3 text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                      {t('stakeholder_types.column_created')}
                    </th>
                    <th className="px-6 py-3 text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                      {t('stakeholder_types.column_actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-card divide-y divide-border">
                  {isLoading ? (
                    <tr>
                      <td colSpan={4} className="px-6 py-12 text-center">
                        <div className="flex items-center justify-center">
                          <div className="motion-safe:animate-spin rounded-full h-6 w-6 border-b-2 border-primary mr-3"></div>
                          <span className="text-muted-foreground">{t('stakeholder_types.loading')}</span>
                        </div>
                      </td>
                    </tr>
                  ) : error ? (
                    <tr>
                      <td colSpan={4} className="px-6 py-12 text-center">
                        <div className="flex flex-col items-center justify-center">
                          <svg className="h-12 w-12 text-destructive mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <p className="text-sm text-destructive mb-2">
                            {t('stakeholder_types.error')}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {(error as any)?.title || (error as any)?.message || 'Unknown error occurred'}
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : stakeholderTypes.length > 0 ? (
                    stakeholderTypes.map((type) => (
                      <tr key={type.uid} className="hover:bg-muted/50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                          <div className="font-medium" data-testid={`stakeholder-type-name-${type.uid}`}>
                            {getStakeholderTypeName(type)}
                          </div>
                          {getStakeholderTypeDescription(type) && (
                            <p className="text-xs text-muted-foreground mt-1 truncate max-w-xs">
                              {getStakeholderTypeDescription(type)}
                            </p>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                          <code className="text-xs bg-muted px-2 py-1 rounded">
                            {type.code}
                          </code>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground" data-testid={`stakeholder-type-created-${type.uid}`}>
                          {formatCreated(type.created_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2">
                            <Link to="/$org/contracts" params={{ org: org || 'demo' }} search={{ stakeholder_type_uid: type.uid } as any}>
                              <Button
                                variant="ghost"
                                size="sm"
                                data-testid={`view-contracts-${type.uid}`}
                              >
                                <AppIcon path={mdiEyeOutline} size="sm" />
                              </Button>
                            </Link>
                            <Link to={`/${org}/contract-stakeholder-types/${type.uid}` as any}>
                              <Button
                                variant="ghost"
                                size="sm"
                                data-testid={`manage-${type.uid}`}
                              >
                                <AppIcon path={mdiCog} size="sm" />
                              </Button>
                            </Link>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={4} className="px-6 py-12 text-center">
                        <div className="flex flex-col items-center justify-center">
                          <svg className="h-12 w-12 text-muted-foreground mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                          </svg>
                          <p className="text-sm text-muted-foreground mb-4">
                            {t('stakeholder_types.empty_title')}
                          </p>
                          <p className="text-xs text-muted-foreground mb-4">
                            {t('stakeholder_types.empty_description')}
                          </p>
                          <Can perm="configuration.write">
                            <Link to={`/${org}/contract-stakeholder-types/create` as any}>
                              <Button data-testid="create-first-stakeholder-type">
                                <AppIcon path={mdiPlus} size="sm" className="mr-2" />
                                {t('stakeholder_types.create')}
                              </Button>
                            </Link>
                          </Can>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}